import { useLayoutEffect } from 'react'

/**
 * **Auf einem Blatt rollt nur die Seite** — 7.2 Abschnitt 26, R915.
 *
 * Der Befund des Auftraggebers vom 2026-10-03: *„Die doppelten Scrollbalken
 * sind etwas hinderlich. Reicht nicht der Balken ganz rechts?"* Im
 * Stammdatenblatt der Gesellschaft standen vierzehn Eigenschaften in einem
 * Rahmen von höchstens 70 % der Schirmhöhe — knapp zu lang, also zwei Balken
 * nebeneinander, und das Mausrad rollte mal die Tabelle, mal die Seite.
 *
 * **Warum das nicht im Stilblatt allein steht.** Ein Kopf bleibt an dem Kasten
 * stehen, der rollt. Soll er an der *Seite* stehen bleiben, darf der Rahmen
 * selbst kein Rollkasten sein — und genau das muss er werden, sobald die
 * Tabelle breiter ist als der Platz, sonst schöbe sie die ganze Seite zur
 * Seite. Ob sie breiter ist, weiß kein Stilblatt; es wird gemessen.
 *
 * Zwei Kennzeichen, beide am Rahmen:
 * - `amblatt` — der Rahmen steht unmittelbar in der rollenden Seite. In einer
 *   Seitenspalte oder auf einer Tafel (R412) trägt er es nicht und behält
 *   dort, was er hatte.
 * - `imfenster` — der Rahmen steht in einem Fenster, das selbst rollt. Dort
 *   gilt dasselbe wie auf dem Blatt (Nachtrag vom 2026-10-03): Der
 *   Wertenachweis zeigte bei niedrigem Bildschirm zwei Balken ineinander.
 * - `imrahmen` — der Rahmen steht in einer Tabelle, die schon rollt: die
 *   Abstimmung unter der Kapitalflussrechnung. Auch dort rollt nur das Äußere.
 * - `breit` — die Tabelle passt in der Breite nicht. Dann rollt der Rahmen
 *   seitwärts, und der Kopf wandert mit der Seite nach oben weg; das ist der
 *   benannte Preis.
 *
 * `--kopfkante` sagt dem Kopf, wie tief er stehen bleibt: unter der
 * Kontextzeile, wo es eine gibt.
 */

/** Der nächste Kasten zwischen Rahmen und Seite, der rollt — an ihm klebt der Kopf. */
function rollkasten(rahmen: HTMLElement): HTMLElement | null {
  for (let kasten = rahmen.parentElement; kasten !== null; kasten = kasten.parentElement) {
    if (kasten === document.body) {
      return null
    }

    const rollen = getComputedStyle(kasten).overflowY
    if (rollen !== '' && rollen !== 'visible' && rollen !== 'clip') {
      return kasten
    }
  }

  return null
}

/**
 * **Ein Fenster liegt fest über der Seite**, eine Seitenspalte und der
 * Rahmen einer Tafel rollen mit ihr oder stehen in ihr. Daran wird es
 * erkannt — nicht an einer Liste von Fensternamen, die beim nächsten Fenster
 * unvollständig wäre.
 */
function istFenster(kasten: HTMLElement): boolean {
  for (let k: HTMLElement | null = kasten; k !== null && k !== document.body; k = k.parentElement) {
    if (getComputedStyle(k).position === 'fixed') {
      return true
    }
  }

  return false
}

/** Der Kopf des Fensters, wenn er beim Rollen stehen bleibt: Unter ihm steht der Tabellenkopf. */
function fensterkopf(kasten: HTMLElement): number {
  const kopf = kasten.querySelector<HTMLElement>(':scope > header')

  if (kopf === null || getComputedStyle(kopf).position !== 'sticky') {
    // Ohne stehenden Fensterkopf klebt der Tabellenkopf ganz oben am Fenster
    // und nicht erst unter dessen Rand — sonst liefen im Rand darüber die
    // Zeilen sichtbar durch (Kursvorschau, 2026-10-03).
    return -(parseFloat(getComputedStyle(kasten).paddingTop) || 0)
  }

  // Der Kopf steht um den Rand des Fensters höher, als er misst (stil.css).
  return kopf.getBoundingClientRect().height + (parseFloat(getComputedStyle(kopf).top) || 0)
}

/** Setzt die Kennzeichen an jeden Rahmen unter der Wurzel. */
export function rahmenMessen(wurzel: ParentNode, kontextzeile: number): HTMLElement[] {
  const rahmenliste = Array.from(wurzel.querySelectorAll<HTMLElement>('.tabellenrahmen'))

  for (const rahmen of rahmenliste) {
    const kasten = rollkasten(rahmen)
    const amBlatt = kasten === null
    // Im Fenster gilt dasselbe wie auf dem Blatt (Nachtrag vom selben Tag):
    // Es rollt das Fenster, nicht die Tabelle darin.
    const imFenster = kasten !== null && istFenster(kasten)
    // Ein Punkt Spiel: Gebrochene Breiten runden auf beiden Seiten verschieden.
    // Nur auf dem Blatt: Im Fenster rollt auch seitwärts das Fenster.
    const breit = amBlatt && rahmen.scrollWidth > rahmen.clientWidth + 1

    // Und in einer Tabelle, die schon rollt (zweiter Nachtrag): die Abstimmung
    // unter der Kapitalflussrechnung.
    const imRahmen = kasten !== null && !imFenster && kasten.classList.contains('tabellenrahmen')

    rahmen.classList.toggle('amblatt', amBlatt)
    rahmen.classList.toggle('imrahmen', imRahmen)
    rahmen.classList.toggle('imfenster', imFenster)
    rahmen.classList.toggle('breit', breit)

    // Rollt der Rahmen seitwärts, klebt der Kopf wieder in ihm — und stünde
    // um die Höhe der Kontextzeile zu tief, mitten in den Zeilen.
    // In einer rollenden Tabelle bleibt es bei null: Die innere steht unter
    // der äußeren, und deren Kopf endet mit ihr.
    const kante = breit ? 0 : amBlatt ? kontextzeile : imFenster ? fensterkopf(kasten) : 0
    rahmen.style.setProperty('--kopfkante', `${kante}px`)
  }

  return rahmenliste
}

/**
 * Hält die Kennzeichen aktuell — einmal in der Schale, für jeden Bildschirm.
 * Gemessen wird, wenn sich der Inhalt ändert, das Fenster seine Größe wechselt
 * oder ein Rahmen selbst breiter oder schmaler wird (die Leiste klappt ein).
 */
export function useTabellenrahmen(): void {
  useLayoutEffect(() => {
    let angesetzt = 0
    const beobachtet = new Set<HTMLElement>()

    const groesse =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => ansetzen())

    function messen() {
      angesetzt = 0
      const kontextzeile = document.querySelector<HTMLElement>('.kontextzeile')
      const rahmenliste = rahmenMessen(document, kontextzeile?.getBoundingClientRect().height ?? 0)

      if (groesse === null) {
        return
      }

      for (const rahmen of beobachtet) {
        if (!rahmen.isConnected) {
          groesse.unobserve(rahmen)
          beobachtet.delete(rahmen)
        }
      }

      for (const rahmen of rahmenliste) {
        if (!beobachtet.has(rahmen)) {
          beobachtet.add(rahmen)
          groesse.observe(rahmen)
        }
      }
    }

    function ansetzen() {
      if (angesetzt === 0) {
        angesetzt = requestAnimationFrame(messen)
      }
    }

    // Der erste Durchgang sofort und nicht im nächsten Bild: Sonst stünde der
    // Rahmen einen Augenblick mit seinem alten Balken da.
    messen()

    const inhalt = new MutationObserver(messen)
    inhalt.observe(document.body, { childList: true, subtree: true })
    window.addEventListener('resize', ansetzen)

    return () => {
      inhalt.disconnect()
      groesse?.disconnect()
      window.removeEventListener('resize', ansetzen)
      if (angesetzt !== 0) {
        cancelAnimationFrame(angesetzt)
      }
    }
  }, [])
}
